import React from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import {
  authEvents,
  isAuthenticated as isUserAuthenticated,
  logoutUser,
} from "../utils/auth";

const Navbar = () => {
  const [open, setOpen] = React.useState(false);
  const [logoFailed, setLogoFailed] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const syncAuth = () => {
      setIsAuthenticated(isUserAuthenticated());
    };

    syncAuth();
    window.addEventListener("storage", syncAuth);
    window.addEventListener(authEvents.AUTH_CHANGED_EVENT, syncAuth);

    return () => {
      window.removeEventListener("storage", syncAuth);
      window.removeEventListener(authEvents.AUTH_CHANGED_EVENT, syncAuth);
    };
  }, []);

  const drawerWidth = 250;
  const rightLinks = [
    { label: "Resume Templates", to: "/templates" },
    { label: "PDF Editor", to: "/pdf-editor" },
    { label: "Image Editor", to: "/image-editor" },
  ];

  const handleLogout = React.useCallback(async () => {
    await logoutUser();
    navigate("/", { replace: true });
  }, [navigate]);

  const isActivePath = React.useCallback(
    (to) => {
      const { pathname } = location;

      if (to === "/") {
        return pathname === "/";
      }

      if (to === "/job-search") {
        return pathname === "/job-search" || pathname.startsWith("/jobs/");
      }

      return pathname === to || pathname.startsWith(`${to}/`);
    },
    [location]
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          height: 60,
          minHeight: "60px !important",
          justifyContent: "center",
          backgroundColor: "navbar.bg",
          color: "navbar.text",
          borderBottom: "1px solid",
          borderColor: "navbar.border",
        }}
      >
        <Toolbar
          sx={{
            minHeight: "60px !important",
            height: 60,
            px: 2,
            display: "flex",
            alignItems: "center",
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => setOpen(!open)}
            sx={{
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "navbar.text",
            }}
          >
            <MenuIcon fontSize="medium" />
          </IconButton>

          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              pl: 1,
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => navigate("/")}
              sx={{
                border: 0,
                p: 0,
                m: 0,
                background: "transparent",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                maxHeight: "32px",
                minWidth: 0,
              }}
            >
              {!logoFailed ? (
                <Box
                  component="img"
                  src={logo}
                  alt="SmartResumeHub"
                  onError={() => setLogoFailed(true)}
                  sx={{
                    width: "auto",
                    height: "auto",
                    maxHeight: { xs: "26px", sm: "32px" },
                    objectFit: "contain",
                    display: "block",
                    "@media (max-width:360px)": {
                      maxHeight: "22px",
                    },
                  }}
                />
              ) : (
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.15rem" },
                    color: "navbar.text",
                    textTransform: "none",
                    lineHeight: 1,
                  }}
                >
                  SmartResumeHub
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1 }}>
            {rightLinks.map((item) => (
              <Button
                key={item.to}
                color="inherit"
                component={Link}
                to={item.to}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  color: isActivePath(item.to) ? "navbar.hover" : "navbar.text",
                  borderBottom: "2px solid",
                  borderColor: isActivePath(item.to) ? "navbar.hover" : "transparent",
                  borderRadius: 0,
                  "&:hover": { color: "navbar.hover", backgroundColor: "transparent" },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          <Box sx={{ display: "flex", gap: 0.5, ml: { xs: 0.5, md: 1 } }}>
            {isAuthenticated ? (
              <Button
                color="inherit"
                onClick={handleLogout}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "auto",
                  px: { xs: 0.75, sm: 1.25 },
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  color: "navbar.text",
                  borderBottom: "2px solid",
                  borderColor: "transparent",
                  borderRadius: 0,
                  "&:hover": { color: "navbar.hover", backgroundColor: "transparent" },
                }}
              >
                Logout
              </Button>
            ) : (
              <Button
                color="inherit"
                component={Link}
                to="/login"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "auto",
                  px: { xs: 0.75, sm: 1.25 },
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  color: isActivePath("/login") ? "navbar.hover" : "navbar.text",
                  borderBottom: "2px solid",
                  borderColor: isActivePath("/login") ? "navbar.hover" : "transparent",
                  borderRadius: 0,
                  "&:hover": { color: "navbar.hover", backgroundColor: "transparent" },
                }}
              >
                Login
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Toolbar sx={{ minHeight: "60px !important", height: 60 }} />
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        anchor="left"
        PaperProps={{
          sx: {
            width: drawerWidth,
            mt: "60px",
            height: "calc(100% - 60px)",
            backgroundColor: "background.paper",
            borderRight: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        <Box sx={{ width: drawerWidth }} role="presentation" onClick={() => setOpen(false)}>
          <List>
            <ListItemButton
              component={Link}
              to="/"
              sx={{
                backgroundColor: isActivePath("/") ? "action.selected" : "transparent",
                "&:hover": { backgroundColor: "card.hover" },
              }}
            >
              <ListItemText primary="Home" />
            </ListItemButton>
            <ListItemButton
              component={Link}
              to="/job-search"
              sx={{
                backgroundColor: isActivePath("/job-search") ? "action.selected" : "transparent",
                "&:hover": { backgroundColor: "card.hover" },
              }}
            >
              <ListItemText primary="Job Search" />
            </ListItemButton>
            <ListItemButton
              component={Link}
              to="/resume-builder"
              sx={{
                backgroundColor: isActivePath("/resume-builder") ? "action.selected" : "transparent",
                "&:hover": { backgroundColor: "card.hover" },
              }}
            >
              <ListItemText primary="Build Resume" />
            </ListItemButton>
            {rightLinks.map((item) => (
              <ListItemButton
                key={item.to}
                component={Link}
                to={item.to}
                sx={{
                  backgroundColor: isActivePath(item.to) ? "action.selected" : "transparent",
                  "&:hover": { backgroundColor: "card.hover" },
                }}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
